import type { SaveWorkflowDraft } from '@Application/Organizations/Workflows/Commands/SaveOrganizationWorkflow';
import areDocumentsValid from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/constraint/areDocumentsValid';
import areReevaluationsSupported from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/constraint/areReevaluationsSupported';
import hasDocumentsInEveryStage from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/constraint/hasDocumentsInEveryStage';
import hasStages from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/constraint/hasStages';
import hasUniqueDocumentTypes from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/constraint/hasUniqueDocumentTypes';
import DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import type OrganizationWorkflowView from '@UI/Store/OrganizationWorkflows/Views/OrganizationWorkflowView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

export type AutomaticSourceDraft = {
	readonly documentTypeId: number;
	readonly verifiedOnly: boolean;
};

type Stages = readonly (readonly DocumentSettingsFormData[])[];

type WorkflowFormArgs = {
	label: string;
	/** Empty means no description. */
	description: string;
	acceptsInterviews: boolean;
	acceptsNotes: boolean;
	acceptsDocuments: boolean;
	requiresValidationBeforeNext: boolean;
	automaticSources: readonly AutomaticSourceDraft[];
	stages: Stages;
};

const freezeStages = (stages: Stages): Stages =>
	Object.freeze(stages.map((stage) => Object.freeze([...stage])));

const collectDocumentTypeIds = (stages: Stages): readonly number[] =>
	stages
		.flat()
		.map((document) => document.documentTypeId)
		.filter((documentTypeId): documentTypeId is number => documentTypeId !== null);

/**
 * Immutable form state of a document group: identity, accepted preparation elements, the documents
 * taken over automatically and the ordered stages of documents. Every structural edit returns a new
 * instance whose document inputs are re-limited to what the group still provides, so a reference to
 * a removed document never survives.
 */
@ValidatedClass()
class WorkflowFormData extends AbstractValidatedClass<WorkflowFormArgs> {
	@constraint(Joi.string().max(180), () => 'Le nom ne peut pas dépasser 180 caractères.')
	@constraint(Joi.string().pattern(/\S/).required(), () => 'Le nom du groupe est requis.')
	public readonly label: string;

	public readonly description: string;

	public readonly acceptsInterviews: boolean;
	public readonly acceptsNotes: boolean;
	public readonly acceptsDocuments: boolean;
	public readonly requiresValidationBeforeNext: boolean;
	public readonly automaticSources: readonly AutomaticSourceDraft[];

	@constraint(
		areReevaluationsSupported,
		() => 'La réévaluation d’un document exige que ce type soit repris automatiquement par le groupe.',
	)
	@constraint(areDocumentsValid, () => 'Un document est mal réglé : ouvrez ses réglages pour le corriger.')
	@constraint(
		hasUniqueDocumentTypes,
		() => 'Un type de document ne peut apparaître qu’une seule fois dans le groupe.',
	)
	@constraint(hasDocumentsInEveryStage, () => 'Ajoutez au moins un type de document à chaque étape.')
	@constraint(hasStages, () => 'Ajoutez au moins une étape.')
	public readonly stages: Stages;

	constructor(data: WorkflowFormArgs) {
		super(data);
		this.label = data.label;
		this.description = data.description;
		this.acceptsInterviews = data.acceptsInterviews;
		this.acceptsNotes = data.acceptsNotes;
		this.acceptsDocuments = data.acceptsDocuments;
		this.requiresValidationBeforeNext = data.requiresValidationBeforeNext;
		this.automaticSources = Object.freeze([...data.automaticSources]);
		this.stages = freezeStages(data.stages);
		Object.freeze(this);
	}

	static createEmpty(): WorkflowFormData {
		return new WorkflowFormData({
			label: '',
			description: '',
			acceptsInterviews: true,
			acceptsNotes: true,
			acceptsDocuments: true,
			requiresValidationBeforeNext: false,
			automaticSources: [],
			stages: [],
		});
	}

	static fromView(workflow: OrganizationWorkflowView): WorkflowFormData {
		return new WorkflowFormData({
			label: workflow.label,
			description: workflow.description ?? '',
			acceptsInterviews: workflow.preparation?.acceptsInterviews ?? true,
			acceptsNotes: workflow.preparation?.acceptsNotes ?? true,
			acceptsDocuments: workflow.preparation?.acceptsDocuments ?? true,
			requiresValidationBeforeNext: workflow.preparation?.requiresValidationBeforeNext ?? false,
			automaticSources: (workflow.preparation?.automaticSources ?? []).map((source) => ({
				documentTypeId: source.documentTypeId,
				verifiedOnly: source.verifiedOnly,
			})),
			stages: workflow.stages.map((stage) =>
				stage.steps.map((step) => DocumentSettingsFormData.fromStep(step)),
			),
		});
	}

	/** Every document type the group produces, in stage order. */
	get documentTypeIds(): readonly number[] {
		return collectDocumentTypeIds(this.stages);
	}

	get automaticDocumentTypeIds(): readonly number[] {
		return this.automaticSources.map((source) => source.documentTypeId);
	}

	/** The types produced by the stages before `stageIndex`: what a document of that stage may receive. */
	previousDocumentTypeIds(stageIndex: number): readonly number[] {
		return collectDocumentTypeIds(this.stages.slice(0, stageIndex));
	}

	findDocument(stageIndex: number, stepIndex: number): DocumentSettingsFormData | null {
		return this.stages[stageIndex]?.[stepIndex] ?? null;
	}

	setLabel(label: string): this {
		return this.cloneWith({ label } as Partial<WorkflowFormArgs>);
	}

	setDescription(description: string): this {
		return this.cloneWith({ description } as Partial<WorkflowFormArgs>);
	}

	setAcceptsInterviews(acceptsInterviews: boolean): this {
		return this.cloneWith({ acceptsInterviews } as Partial<WorkflowFormArgs>);
	}

	setAcceptsNotes(acceptsNotes: boolean): this {
		return this.cloneWith({ acceptsNotes } as Partial<WorkflowFormArgs>);
	}

	setAcceptsDocuments(acceptsDocuments: boolean): this {
		return this.cloneWith({ acceptsDocuments } as Partial<WorkflowFormArgs>);
	}

	setRequiresValidationBeforeNext(requiresValidationBeforeNext: boolean): this {
		return this.cloneWith({ requiresValidationBeforeNext } as Partial<WorkflowFormArgs>);
	}

	addAutomaticSource(documentTypeId: number): this {
		if (this.automaticDocumentTypeIds.includes(documentTypeId)) {
			return this;
		}

		return this.withAutomaticSources([...this.automaticSources, { documentTypeId, verifiedOnly: false }]);
	}

	removeAutomaticSource(documentTypeId: number): this {
		return this.withAutomaticSources(
			this.automaticSources.filter((source) => source.documentTypeId !== documentTypeId),
		);
	}

	/** Swaps the type of a taken-over document; a type already taken over leaves the group unchanged. */
	changeAutomaticSourceType(documentTypeId: number, nextDocumentTypeId: number): this {
		if (this.automaticDocumentTypeIds.includes(nextDocumentTypeId)) {
			return this;
		}

		return this.withAutomaticSources(
			this.automaticSources.map((source) =>
				source.documentTypeId === documentTypeId
					? { ...source, documentTypeId: nextDocumentTypeId }
					: source,
			),
		);
	}

	setAutomaticSourceVerifiedOnly(documentTypeId: number, verifiedOnly: boolean): this {
		return this.withAutomaticSources(
			this.automaticSources.map((source) =>
				source.documentTypeId === documentTypeId ? { ...source, verifiedOnly } : source,
			),
		);
	}

	/** Appends a stage after the last one, holding `document`. */
	addStage(document: DocumentSettingsFormData): this {
		return this.withStages([...this.stages, [document]]);
	}

	removeStage(stageIndex: number): this {
		return this.withStages(this.stages.filter((_stage, index) => index !== stageIndex));
	}

	/** Swaps the stage with its neighbour; out-of-range moves return the instance unchanged. */
	moveStage(stageIndex: number, offset: -1 | 1): this {
		const targetIndex = stageIndex + offset;

		if (
			stageIndex < 0 ||
			stageIndex >= this.stages.length ||
			targetIndex < 0 ||
			targetIndex >= this.stages.length
		) {
			return this;
		}

		const stages = [...this.stages];
		const moved = stages[stageIndex];
		const target = stages[targetIndex];

		if (moved === undefined || target === undefined) {
			return this;
		}

		stages[stageIndex] = target;
		stages[targetIndex] = moved;

		return this.withStages(stages);
	}

	addDocument(stageIndex: number, document: DocumentSettingsFormData): this {
		return this.withStages(
			this.stages.map((stage, index) => (index === stageIndex ? [...stage, document] : stage)),
		);
	}

	/** Removes a document; its stage disappears with its last document. */
	removeDocument(stageIndex: number, stepIndex: number): this {
		return this.withStages(
			this.stages
				.map((stage, index) =>
					index === stageIndex
						? stage.filter((_document, position) => position !== stepIndex)
						: stage,
				)
				.filter((stage) => stage.length > 0),
		);
	}

	replaceDocument(stageIndex: number, stepIndex: number, document: DocumentSettingsFormData): this {
		return this.withStages(
			this.stages.map((stage, index) =>
				index === stageIndex
					? stage.map((current, position) => (position === stepIndex ? document : current))
					: stage,
			),
		);
	}

	/** `documentTypeLabelOf` resolves the label a document falls back to when it has none of its own. */
	toDraft(documentTypeLabelOf: (documentTypeId: number) => string): SaveWorkflowDraft {
		const description = this.description.trim();
		const hasAutomaticSources = this.automaticSources.length > 0;

		return {
			label: this.label.trim(),
			description: description === '' ? null : description,
			acceptsInterviews: this.acceptsInterviews,
			acceptsNotes: this.acceptsNotes,
			acceptsDocuments: this.acceptsDocuments,
			requiresValidationBeforeNext: this.requiresValidationBeforeNext,
			automaticSources: this.automaticSources.map((source) => ({
				documentTypeId: source.documentTypeId,
				verifiedOnly: source.verifiedOnly,
			})),
			stages: this.stages.map((stage, stageIndex) => ({
				steps: stage.map((document) =>
					document.toDraftStep(
						document.documentTypeId !== null ? documentTypeLabelOf(document.documentTypeId) : '',
						stageIndex > 0,
						hasAutomaticSources,
					),
				),
			})),
		};
	}

	private withAutomaticSources(automaticSources: readonly AutomaticSourceDraft[]): this {
		return this.cloneWith({
			automaticSources,
			stages: this.limitInputs(
				this.stages,
				automaticSources.map((source) => source.documentTypeId),
			),
		} as Partial<WorkflowFormArgs>);
	}

	private withStages(stages: Stages): this {
		return this.cloneWith({
			stages: this.limitInputs(stages, this.automaticDocumentTypeIds),
		} as Partial<WorkflowFormArgs>);
	}

	private limitInputs(stages: Stages, automaticDocumentTypeIds: readonly number[]): Stages {
		return stages.map((stage, stageIndex) => {
			const previousTypeIds = collectDocumentTypeIds(stages.slice(0, stageIndex));

			return stage.map((document) =>
				document.withInputsLimitedTo(previousTypeIds, automaticDocumentTypeIds),
			);
		});
	}
}

export default WorkflowFormData;
